import { useEffect, useState } from "react";
import { Plus, Mail, Edit3, Trash2, X, Loader2, AlertCircle, Send } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

function emptyForm() {
  return {
    carrier_code: "",
    carrier_name: "",
    email: "",
    cc_emails: "",
    auto_send: true,
    partner_profile: "GENERIC",
    batch_mode: "IMMEDIATE",
    sender_id: "",
    receiver_id: "",
    depot_code: "",
    depot_location: "",
    unh_prefix: "",
    iso_type_map_text: "",
  };
}

function RecipientDialog({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          carrier_code: initial.carrier_code || "",
          carrier_name: initial.carrier_name || "",
          email: initial.email || "",
          cc_emails: (initial.cc_emails || []).join(", "),
          auto_send: initial.auto_send !== false,
          partner_profile: initial.partner_profile || "GENERIC",
          sender_id: initial.sender_id || "",
          receiver_id: initial.receiver_id || "",
          depot_code: initial.depot_code || "",
          depot_location: initial.depot_location || "",
          unh_prefix: initial.unh_prefix || "",
          iso_type_map_text: initial.iso_type_map
            ? Object.entries(initial.iso_type_map).map(([k, v]) => `${k}=${v}`).join(", ")
            : "",
        });
      } else {
        setForm(emptyForm());
      }
      setErr("");
    }
  }, [open, initial]);

  if (!open) return null;
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    const payload = {
      carrier_code: form.carrier_code.toUpperCase().trim(),
      carrier_name: form.carrier_name.trim() || null,
      email: form.email.trim(),
      cc_emails: form.cc_emails ? form.cc_emails.split(",").map((s) => s.trim()).filter(Boolean) : [],
      auto_send: !!form.auto_send,
      partner_profile: form.partner_profile || "GENERIC",
      batch_mode: form.batch_mode || "IMMEDIATE",
      sender_id: form.sender_id.trim() || null,
      receiver_id: form.receiver_id.trim() || null,
      depot_code: form.depot_code.trim() || null,
      depot_location: form.depot_location.trim() || null,
      unh_prefix: form.unh_prefix.trim() || null,
      iso_type_map: (() => {
        if (!form.iso_type_map_text || !form.iso_type_map_text.trim()) return null;
        const m = {};
        form.iso_type_map_text.split(",").forEach((pair) => {
          const [k, v] = pair.split("=").map((s) => s.trim().toUpperCase());
          if (k && v) m[k] = v;
        });
        return Object.keys(m).length ? m : null;
      })(),
    };
    try {
      if (initial?.id) {
        await api.patch(`/edi-recipients/${initial.id}`, payload);
        toast.success("Recipient updated");
      } else {
        await api.post("/edi-recipients", payload);
        toast.success("Recipient added");
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="recipient-dialog">
      <div className="bg-white border border-gray-200 w-full max-w-lg">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{initial ? "Edit" : "Add"} mapping</div>
            <div className="font-display font-extrabold text-lg">Carrier → EDI recipient</div>
          </div>
          <button onClick={onClose} className="ps-btn ps-btn-secondary"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {err && (
            <div className="flex items-start gap-2 border border-red-200 bg-red-50 px-3 py-2 rounded-sm text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Carrier code <span className="text-red-500">*</span></label>
              <input required value={form.carrier_code} onChange={(e) => update("carrier_code", e.target.value.toUpperCase())}
                placeholder="HLC, ARK, MSC…" className="ps-input font-mono" data-testid="recipient-carrier-code" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Carrier name</label>
              <input value={form.carrier_name} onChange={(e) => update("carrier_name", e.target.value)}
                placeholder="Hapag-Lloyd" className="ps-input" data-testid="recipient-carrier-name" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Recipient email <span className="text-red-500">*</span></label>
            <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
              placeholder="edi-gateway@carrier.com" className="ps-input font-mono" data-testid="recipient-email" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">CC emails (comma separated)</label>
            <input value={form.cc_emails} onChange={(e) => update("cc_emails", e.target.value)}
              placeholder="ops@portside.nl, manager@portside.nl" className="ps-input font-mono" data-testid="recipient-cc" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.auto_send} onChange={(e) => update("auto_send", e.target.checked)} data-testid="recipient-auto-send" />
            <span>Auto-send EDI on every gate event for this carrier</span>
          </label>

          <div className="border-t border-gray-200 pt-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">Partner profile (advanced)</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Profile</label>
                <select value={form.partner_profile} onChange={(e) => update("partner_profile", e.target.value)} className="ps-input" data-testid="recipient-profile">
                  <option value="GENERIC">Generic CODECO95B</option>
                  <option value="HAPAG_LLOYD">Hapag-Lloyd (ITG14)</option>
                  <option value="ARKAS">ARKAS Line</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Send mode</label>
                <select value={form.batch_mode} onChange={(e) => update("batch_mode", e.target.value)} className="ps-input" data-testid="recipient-batch-mode">
                  <option value="IMMEDIATE">Immediate — one EDI per gate event</option>
                  <option value="BATCHED">Batched — queue events, send bundle on flush</option>
                </select>
                <div className="text-[11px] text-gray-500 mt-1">Batched mode is great for high-volume carriers — bundles multiple gate events into one interchange.</div>
              </div>
              {form.partner_profile !== "GENERIC" && (
                <>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Depot sender ID (UNB)</label>
                    <input value={form.sender_id} onChange={(e) => update("sender_id", e.target.value.toUpperCase())}
                      placeholder={form.partner_profile === "ARKAS" ? "GHPRT" : "GHMTG"}
                      className="ps-input font-mono" data-testid="recipient-sender-id" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Partner receiver ID (UNB)</label>
                    <input value={form.receiver_id} onChange={(e) => update("receiver_id", e.target.value.toUpperCase())}
                      placeholder={form.partner_profile === "ARKAS" ? "ARK" : "HLCU"}
                      className="ps-input font-mono" data-testid="recipient-receiver-id" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Depot short code (LOC/UNH)</label>
                    <input value={form.depot_code} onChange={(e) => update("depot_code", e.target.value.toUpperCase())} placeholder="MTG" className="ps-input font-mono" data-testid="recipient-depot-code" />
                  </div>
                  {form.partner_profile === "HAPAG_LLOYD" && (
                    <>
                      <div>
                        <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Depot location code (LOC)</label>
                        <input value={form.depot_location} onChange={(e) => update("depot_location", e.target.value.toUpperCase())} placeholder="GHTEM" className="ps-input font-mono" data-testid="recipient-depot-loc" />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">UNH prefix (8 digits)</label>
                        <input value={form.unh_prefix} onChange={(e) => update("unh_prefix", e.target.value)} placeholder="31811167" className="ps-input font-mono" data-testid="recipient-unh-prefix" />
                      </div>
                    </>
                  )}
                  <div className="col-span-2">
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">ISO → partner type map (override)</label>
                    <input
                      value={form.iso_type_map_text}
                      onChange={(e) => update("iso_type_map_text", e.target.value)}
                      placeholder={form.partner_profile === "ARKAS" ? "22G1=22G1, 45G1=45G1 (ARKAS uses ISO codes)" : "22G1=G122, 45G1=G142, 45U1=U142"}
                      className="ps-input font-mono text-xs"
                      data-testid="recipient-iso-map"
                    />
                    <div className="text-[11px] text-gray-500 mt-1">{form.partner_profile === "ARKAS" ? "ARKAS uses standard ISO codes — leave blank unless you have custom overrides." : "Defaults from HL ITG14 codelist are used if blank."}</div>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="ps-btn ps-btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="ps-btn ps-btn-primary" data-testid="recipient-submit">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Send className="w-4 h-4" /> Save</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function EdiRecipients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState({});
  const [flushing, setFlushing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/edi-recipients"),
        api.get("/edi/pending-counts"),
      ]);
      setItems(r1.data);
      setPending(r2.data || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (rec) => {
    if (!window.confirm(`Delete EDI mapping for ${rec.carrier_code}?`)) return;
    try {
      await api.delete(`/edi-recipients/${rec.id}`);
      toast.success("Mapping deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onFlush = async (rec) => {
    setFlushing(rec.carrier_code);
    try {
      const { data } = await api.post(`/edi/flush-batch/${rec.carrier_code}`);
      if (data.ok) {
        toast.success(`Flushed ${data.flushed} events for ${rec.carrier_code} — bundled & sent`);
      } else {
        toast.error(`Flush failed: ${data.edi_message?.last_error?.slice(0, 80) || "unknown"}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFlushing(null);
    }
  };

  return (
    <div data-testid="edi-recipients-page">
      <PageHeader
        title="EDI Recipients"
        subtitle="Per-carrier recipient email mapping. Gate events with a mapped carrier auto-send CODECO95B."
        testid="edi-recipients-header"
        actions={
          <button className="ps-btn ps-btn-primary" onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-recipient">
            <Plus className="w-4 h-4" /> Add mapping
          </button>
        }
      />
      <div className="p-6 sm:p-8 space-y-4">
        <div className="border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid="edi-unified-banner">
          <Mail className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>Unified storage:</strong> EDI config now lives on the carrier's partner record.
            This page is a quick read-only roll-up of all carriers with EDI configured — to edit the full mapping
            (incl. CC emails, send mode, depot codes), open the carrier on the <a href="/partners" className="font-bold underline">Partners</a> page.
          </div>
        </div>
        <div className="border border-gray-200 bg-white">
          <table className="ps-table" data-testid="recipients-table">
            <thead>
              <tr>
                <th>Carrier</th>
                <th>Carrier name</th>
                <th>Profile</th>
                <th>Mode</th>
                <th>Recipient</th>
                <th>Auto-send</th>
                <th>Pending</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center text-gray-400 py-10">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-gray-400 py-10">
                    <Mail className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    No mappings yet. Add one so EDI emails fire automatically on gate-in / gate-out.
                  </td>
                </tr>
              )}
              {!loading && items.map((r) => {
                const profileMap = {
                  HAPAG_LLOYD: { cls: "ps-pill ps-pill-info", label: "Hapag-Lloyd" },
                  ARKAS: { cls: "ps-pill ps-pill-warning", label: "ARKAS" },
                };
                const profile = profileMap[r.partner_profile] || { cls: "ps-pill ps-pill-neutral", label: "Generic" };
                const pendingCount = pending[r.carrier_code] || 0;
                return (
                  <tr key={r.id} data-testid={`recipient-row-${r.carrier_code}`}>
                    <td className="font-mono font-bold text-gray-900">{r.carrier_code}</td>
                    <td>{r.carrier_name || "—"}</td>
                    <td><span className={profile.cls}><span className="dot" />{profile.label}</span></td>
                    <td>
                      {r.batch_mode === "BATCHED"
                        ? <span className="ps-pill ps-pill-warning"><span className="dot" />Batched</span>
                        : <span className="ps-pill ps-pill-neutral"><span className="dot" />Immediate</span>}
                    </td>
                    <td className="font-mono">{r.email}</td>
                    <td>
                      {r.auto_send
                        ? <span className="ps-pill ps-pill-success"><span className="dot" />ON</span>
                        : <span className="ps-pill ps-pill-neutral"><span className="dot" />OFF</span>}
                    </td>
                    <td>
                      {pendingCount > 0 ? (
                        <button
                          onClick={() => onFlush(r)}
                          disabled={flushing === r.carrier_code}
                          className="ps-btn ps-btn-primary h-7 px-2 text-xs"
                          data-testid={`flush-${r.carrier_code}`}
                        >
                          {flushing === r.carrier_code ? "Flushing…" : <><Send className="w-3 h-3" /> Flush {pendingCount}</>}
                        </button>
                      ) : <span className="text-xs text-gray-400 font-mono">0</span>}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <button className="ps-btn ps-btn-secondary h-8 px-3 text-xs mr-1" onClick={() => { setEditing(r); setOpen(true); }} data-testid={`edit-${r.carrier_code}`}>
                        <Edit3 className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button className="ps-btn ps-btn-danger h-8 px-3 text-xs" onClick={() => onDelete(r)} data-testid={`delete-${r.carrier_code}`}>
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <RecipientDialog open={open} initial={editing} onClose={() => setOpen(false)} onSaved={load} />
    </div>
  );
}
